import React, { useState, useEffect, useCallback, useRef } from 'react';

interface Step {
  title: string;
  description: string;
  illustration: React.ReactNode;
  accent: string;
  glow: string;
}

const STEP_DURATION = 3500;
const TRANSITION_DURATION = 800;
const OVERVIEW_DURATION = 4000;

const steps: Step[] = [
  {
    title: 'Сбор данных',
    description:
      'Укажите дату, время и место рождения. Чем точнее время — тем более детальной будет ваша натальная карта. Мы используем проверенные базы данных городов и часовых поясов, чтобы каждый расчёт был корректным.',
    accent: '#d4af37',
    glow: 'rgba(212, 175, 55, 0.4)',
    illustration: (
      <svg viewBox="0 0 200 200" width="160" height="160">
        <defs>
          <radialGradient id="hiw-glow1" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#d4af37" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#d4af37" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="100" cy="100" r="90" fill="url(#hiw-glow1)" />
        <circle cx="100" cy="100" r="60" fill="none" stroke="#d4af37" strokeWidth="1" opacity="0.3" />
        <circle cx="100" cy="100" r="40" fill="none" stroke="#d4af37" strokeWidth="0.5" opacity="0.2" />
        <rect x="70" y="65" width="60" height="55" rx="6" fill="none" stroke="#d4af37" strokeWidth="2" />
        <line x1="70" y1="80" x2="130" y2="80" stroke="#d4af37" strokeWidth="1.5" />
        <line x1="85" y1="60" x2="85" y2="70" stroke="#d4af37" strokeWidth="2" strokeLinecap="round" />
        <line x1="115" y1="60" x2="115" y2="70" stroke="#d4af37" strokeWidth="2" strokeLinecap="round" />
        <circle cx="100" cy="100" r="12" fill="none" stroke="#f4d03f" strokeWidth="1.5" />
        <line x1="100" y1="100" x2="100" y2="92" stroke="#f4d03f" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="100" y1="100" x2="107" y2="103" stroke="#f4d03f" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M145 90 C145 80, 155 80, 155 90 C155 100, 150 108, 150 108 C150 108, 145 100, 145 90Z" fill="#d4af37" opacity="0.7" />
        <circle cx="150" cy="89" r="3" fill="#0A0814" />
      </svg>
    ),
  },
  {
    title: 'Астрономический расчёт',
    description:
      'Библиотека Swiss Ephemeris — мировой стандарт астрологических вычислений — рассчитывает положения Солнца, Луны и планет в момент вашего рождения с точностью до минуты. Учитываются поправки на нутацию, аберрацию и рефракцию.',
    accent: '#9B59B6',
    glow: 'rgba(155, 89, 182, 0.4)',
    illustration: (
      <svg viewBox="0 0 200 200" width="160" height="160">
        <defs>
          <radialGradient id="hiw-glow2" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#9B59B6" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#9B59B6" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="100" cy="100" r="90" fill="url(#hiw-glow2)" />
        <ellipse cx="100" cy="100" rx="80" ry="30" fill="none" stroke="#9B59B6" strokeWidth="0.5" opacity="0.3" transform="rotate(-20 100 100)" />
        <ellipse cx="100" cy="100" rx="60" ry="22" fill="none" stroke="#9B59B6" strokeWidth="0.5" opacity="0.25" transform="rotate(15 100 100)" />
        <ellipse cx="100" cy="100" rx="40" ry="15" fill="none" stroke="#9B59B6" strokeWidth="0.5" opacity="0.2" transform="rotate(-5 100 100)" />
        <circle cx="100" cy="100" r="14" fill="#d4af37" opacity="0.8" />
        <circle cx="100" cy="100" r="18" fill="none" stroke="#f4d03f" strokeWidth="0.5" opacity="0.4" />
        <circle cx="45" cy="85" r="5" fill="#8B9DC3" opacity="0.9" />
        <circle cx="155" cy="110" r="4" fill="#E07B54" opacity="0.9" />
        <circle cx="120" cy="65" r="3.5" fill="#C9B37E" opacity="0.9" />
        <circle cx="75" cy="130" r="3" fill="#9BC4E2" opacity="0.8" />
        <circle cx="30" cy="40" r="1" fill="#fff" opacity="0.6" />
        <circle cx="170" cy="50" r="1.2" fill="#fff" opacity="0.5" />
        <circle cx="160" cy="160" r="0.8" fill="#fff" opacity="0.4" />
        <circle cx="40" cy="160" r="1" fill="#fff" opacity="0.5" />
      </svg>
    ),
  },
  {
    title: 'Интерпретация',
    description:
      'На основе натальной карты нейросеть и команда экспертов готовят персональный анализ: аспекты планет, дома, знаки, ключевые энергии. Каждый текст уникален и привязан именно к вашим данным — никаких общих гороскопов.',
    accent: '#7dd3c0',
    glow: 'rgba(125, 211, 192, 0.4)',
    illustration: (
      <svg viewBox="0 0 200 200" width="160" height="160">
        <defs>
          <radialGradient id="hiw-glow3" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#7dd3c0" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#7dd3c0" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="100" cy="100" r="90" fill="url(#hiw-glow3)" />
        <circle cx="100" cy="80" r="30" fill="none" stroke="#7dd3c0" strokeWidth="1.5" opacity="0.5" />
        <circle cx="85" cy="70" r="4" fill="#7dd3c0" opacity="0.7" />
        <circle cx="115" cy="70" r="4" fill="#7dd3c0" opacity="0.7" />
        <circle cx="100" cy="60" r="3" fill="#d4af37" opacity="0.6" />
        <circle cx="90" cy="90" r="3.5" fill="#7dd3c0" opacity="0.6" />
        <circle cx="110" cy="90" r="3.5" fill="#7dd3c0" opacity="0.6" />
        <line x1="85" y1="70" x2="115" y2="70" stroke="#7dd3c0" strokeWidth="0.8" opacity="0.4" />
        <line x1="85" y1="70" x2="100" y2="60" stroke="#7dd3c0" strokeWidth="0.8" opacity="0.4" />
        <line x1="115" y1="70" x2="100" y2="60" stroke="#7dd3c0" strokeWidth="0.8" opacity="0.4" />
        <line x1="85" y1="70" x2="90" y2="90" stroke="#7dd3c0" strokeWidth="0.8" opacity="0.3" />
        <line x1="115" y1="70" x2="110" y2="90" stroke="#7dd3c0" strokeWidth="0.8" opacity="0.3" />
        <line x1="90" y1="90" x2="110" y2="90" stroke="#7dd3c0" strokeWidth="0.8" opacity="0.3" />
        <line x1="70" y1="130" x2="130" y2="130" stroke="#7dd3c0" strokeWidth="1" opacity="0.3" />
        <line x1="75" y1="140" x2="125" y2="140" stroke="#7dd3c0" strokeWidth="1" opacity="0.25" />
        <line x1="80" y1="150" x2="120" y2="150" stroke="#7dd3c0" strokeWidth="1" opacity="0.2" />
        <path d="M145 55 L148 62 L155 65 L148 68 L145 75 L142 68 L135 65 L142 62Z" fill="#d4af37" opacity="0.6" />
      </svg>
    ),
  },
  {
    title: 'Готовый результат',
    description:
      'Вы получаете структурированный отчёт с расшифровкой личности, сильных сторон, жизненных циклов и практических рекомендаций. Все материалы доступны в личном кабинете в любое время — возвращайтесь к ним, когда понадобится guidance.',
    accent: '#FF9800',
    glow: 'rgba(255, 152, 0, 0.4)',
    illustration: (
      <svg viewBox="0 0 200 200" width="160" height="160">
        <defs>
          <radialGradient id="hiw-glow4" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FF9800" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#FF9800" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="100" cy="100" r="90" fill="url(#hiw-glow4)" />
        <rect x="65" y="50" width="70" height="90" rx="8" fill="none" stroke="#FF9800" strokeWidth="1.5" opacity="0.4" />
        <circle cx="100" cy="80" r="16" fill="none" stroke="#FF9800" strokeWidth="1.5" opacity="0.6" />
        <path d="M91 80 L97 86 L110 73" fill="none" stroke="#d4af37" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        <line x1="80" y1="108" x2="120" y2="108" stroke="#FF9800" strokeWidth="1" opacity="0.4" />
        <line x1="85" y1="118" x2="115" y2="118" stroke="#FF9800" strokeWidth="1" opacity="0.3" />
        <line x1="88" y1="128" x2="112" y2="128" stroke="#FF9800" strokeWidth="1" opacity="0.2" />
        <circle cx="45" cy="60" r="2" fill="#d4af37" opacity="0.5" />
        <circle cx="155" cy="70" r="1.5" fill="#d4af37" opacity="0.4" />
        <circle cx="50" cy="140" r="1.8" fill="#FF9800" opacity="0.4" />
        <circle cx="150" cy="130" r="2" fill="#FF9800" opacity="0.5" />
        <path d="M148 45 L150 50 L155 52 L150 54 L148 59 L146 54 L141 52 L146 50Z" fill="#d4af37" opacity="0.7" />
      </svg>
    ),
  },
];

const HowItWorks: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(-1);
  const [phase, setPhase] = useState<'entering' | 'visible' | 'exiting' | 'overview'>('entering');
  const [isMobile, setIsMobile] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [tilt, setTilt] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  }, []);

  const advanceStep = useCallback(() => {
    setCurrentStep((prev) => {
      const next = prev + 1;
      if (next >= steps.length) { setPhase('overview'); return prev; }
      setPhase('entering');
      return next;
    });
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    clearTimer();
    if (phase === 'entering') {
      timerRef.current = setTimeout(() => setPhase('visible'), TRANSITION_DURATION);
    } else if (phase === 'visible') {
      timerRef.current = setTimeout(() => {
        if (currentStep >= steps.length - 1) setPhase('overview');
        else setPhase('exiting');
      }, STEP_DURATION);
    } else if (phase === 'exiting') {
      timerRef.current = setTimeout(() => advanceStep(), TRANSITION_DURATION);
    } else if (phase === 'overview') {
      timerRef.current = setTimeout(() => { setCurrentStep(0); setPhase('entering'); }, OVERVIEW_DURATION);
    }
    return clearTimer;
  }, [phase, currentStep, isVisible, advanceStep, clearTimer]);

  useEffect(() => {
    if (isVisible && currentStep === -1) { setCurrentStep(0); setPhase('entering'); }
  }, [isVisible, currentStep]);

  const handleCardMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x: y * -8, y: x * 8 });
    setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const handleCardMouseLeave = () => {
    setTilt({ x: 0, y: 0 });
    setMousePos(null);
  };

  const getStepAnimStyle = (): React.CSSProperties => {
    switch (phase) {
      case 'entering':
        return { opacity: 1, transform: 'translateX(0)', transition: `opacity ${TRANSITION_DURATION}ms ease-out, transform ${TRANSITION_DURATION}ms ease-out` };
      case 'visible':
        return { opacity: 1, transform: 'translateX(0)' };
      case 'exiting':
        return { opacity: 0, transform: 'translateX(-60px)', transition: `opacity ${TRANSITION_DURATION}ms ease-in, transform ${TRANSITION_DURATION}ms ease-in` };
      default:
        return { opacity: 0, transform: 'translateX(60px)' };
    }
  };

  const step = currentStep >= 0 && currentStep < steps.length ? steps[currentStep] : null;

  return (
    <section ref={sectionRef} style={containerStyle}>
      <style>{`
        @keyframes hiw-fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes hiw-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes hiw-pulseGlow {
          0%, 100% { filter: drop-shadow(0 0 8px rgba(212,175,55,0.3)); }
          50% { filter: drop-shadow(0 0 20px rgba(212,175,55,0.6)); }
        }
        @keyframes hiw-scaleIn {
          from { opacity: 0; transform: scale(0.85); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes hiw-starFloat {
          0% { transform: translateY(0) scale(1); opacity: 0.6; }
          50% { transform: translateY(-15px) scale(1.3); opacity: 1; }
          100% { transform: translateY(-30px) scale(0.8); opacity: 0; }
        }
        @keyframes hiw-gradientFlow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @media (max-width: 768px) {
          .hiw-step-content { flex-direction: column !important; gap: 20px !important; text-align: center; }
          .hiw-step-content .hiw-text-block { text-align: center; }
          .hiw-overview-grid { flex-direction: column !important; }
        }
      `}</style>

      <h2 style={{
        ...headingStyle,
        ...(isVisible ? { animation: 'hiw-fadeInUp 0.8s ease-out' } : { opacity: 0 }),
      }}>
        Как это работает
      </h2>

      {/* Основная карточка-плитка */}
      <div
        ref={cardRef}
        style={{
          ...cardContainerStyle,
          ...(phase === 'overview' ? {} : {
            transform: `perspective(1000px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
          }),
          ...(step ? {
            borderColor: `${step.accent}30`,
            boxShadow: `0 8px 40px ${step.glow.replace('0.4', '0.15')}, inset 0 0 40px rgba(13, 20, 40, 0.5)`,
          } : {}),
        }}
        onMouseMove={handleCardMouseMove}
        onMouseLeave={handleCardMouseLeave}
      >
        {/* Градиентная рамка */}
        <div style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '24px',
          background: step
            ? `linear-gradient(135deg, ${step.accent}20, transparent 50%)`
            : 'linear-gradient(135deg, rgba(212,175,55,0.1), transparent 50%)',
          pointerEvents: 'none',
          transition: 'background 0.5s ease',
        }} />

        {/* Цветная полоска сверху */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: '10%',
          right: '10%',
          height: '2px',
          background: step
            ? `linear-gradient(90deg, transparent, ${step.accent}, transparent)`
            : 'linear-gradient(90deg, transparent, rgba(212,175,55,0.5), transparent)',
          transition: 'background 0.5s ease',
        }} />

        {/* Spotlight за курсором */}
        {mousePos && (
          <div style={{
            position: 'absolute',
            left: mousePos.x - 100,
            top: mousePos.y - 100,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: step
              ? `radial-gradient(circle, ${step.glow.replace('0.4', '0.15')} 0%, transparent 70%)`
              : 'radial-gradient(circle, rgba(212,175,55,0.1) 0%, transparent 70%)',
            pointerEvents: 'none',
            transition: 'left 0.1s, top 0.1s',
          }} />
        )}

        {phase === 'overview' ? (
          <div className="hiw-overview-grid" style={overviewGridStyle}>
            {steps.map((s, i) => (
              <div key={i} style={{
                ...miniCardStyle,
                animation: `hiw-scaleIn 0.5s ease-out ${i * 0.15}s both`,
                borderColor: `${s.accent}30`,
              }}>
                <div style={{ ...miniNumberStyle, background: `linear-gradient(135deg, ${s.accent}, ${s.accent}cc)` }}>
                  {i + 1}
                </div>
                <div style={{ ...miniTitleStyle, color: s.accent }}>{s.title}</div>
              </div>
            ))}
          </div>
        ) : step ? (
          <div className="hiw-step-content" style={stepContentStyle}>
            <div style={{ ...illustrationWrapStyle, ...getStepAnimStyle() }}>
              <div style={{ animation: 'hiw-float 4s ease-in-out infinite, hiw-pulseGlow 3s ease-in-out infinite' }}>
                {step.illustration}
              </div>
              {/* Частицы */}
              {[...Array(4)].map((_, i) => (
                <div key={i} style={{
                  position: 'absolute',
                  width: '4px',
                  height: '4px',
                  borderRadius: '50%',
                  background: step.accent,
                  left: `${15 + i * 25}%`,
                  bottom: '5%',
                  animation: `hiw-starFloat ${1.5 + i * 0.3}s ease-in-out infinite ${i * 0.2}s`,
                  pointerEvents: 'none',
                }} />
              ))}
            </div>
            <div className="hiw-text-block" style={{ ...textBlockStyle, ...getStepAnimStyle() }}>
              <div style={{
                ...stepNumberStyle,
                background: `linear-gradient(135deg, ${step.accent}, ${step.accent}cc)`,
                boxShadow: `0 0 20px ${step.glow}`,
              }}>
                {currentStep + 1}
              </div>
              <h3 style={{ ...stepTitleStyle, color: step.accent }}>{step.title}</h3>
              <p style={stepDescriptionStyle}>{step.description}</p>
              {/* Прогресс-точки */}
              <div style={dotsStyle}>
                {steps.map((s, i) => (
                  <div key={i} style={{
                    width: i === currentStep ? '24px' : '8px',
                    height: '8px',
                    borderRadius: '4px',
                    background: i === currentStep ? step.accent : 'rgba(255,255,255,0.15)',
                    transition: 'all 0.3s ease',
                  }} />
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
};

// === Styles ===

const containerStyle: React.CSSProperties = {
  padding: '32px 20px',
  maxWidth: '1200px',
  margin: '0 auto',
};

const headingStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontStyle: 'italic',
  fontSize: '36px',
  background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
  backgroundSize: '200% auto',
  animation: 'hiw-gradientFlow 4s ease infinite',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  textAlign: 'center',
  marginBottom: '32px',
  letterSpacing: '0.02em',
};

const cardContainerStyle: React.CSSProperties = {
  position: 'relative',
  background: 'rgba(13, 20, 40, 0.85)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(212, 175, 55, 0.12)',
  borderRadius: '24px',
  padding: '32px 24px',
  minHeight: '280px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  transition: 'transform 0.15s ease-out, box-shadow 0.3s ease, border-color 0.3s ease',
};

const stepContentStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '40px',
  width: '100%',
  position: 'relative',
  zIndex: 1,
};

const illustrationWrapStyle: React.CSSProperties = {
  flex: '0 0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
};

const textBlockStyle: React.CSSProperties = {
  flex: '1',
  maxWidth: '500px',
};

const stepNumberStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  fontFamily: 'var(--font-sans)',
  fontWeight: 700,
  fontSize: '15px',
  color: '#0A0814',
  marginBottom: '12px',
  transition: 'background 0.3s ease, box-shadow 0.3s ease',
};

const stepTitleStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontStyle: 'italic',
  fontSize: '24px',
  fontWeight: 600,
  marginBottom: '12px',
  lineHeight: 1.2,
  transition: 'color 0.3s ease',
};

const stepDescriptionStyle: React.CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: '15px',
  color: '#b0b0b0',
  lineHeight: 1.7,
  margin: '0 0 20px',
};

const dotsStyle: React.CSSProperties = {
  display: 'flex',
  gap: '8px',
};

const overviewGridStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '24px',
  width: '100%',
  position: 'relative',
  zIndex: 1,
  flexWrap: 'wrap',
};

const miniCardStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px 28px',
  background: 'rgba(18, 15, 46, 0.5)',
  backdropFilter: 'blur(12px)',
  borderRadius: '16px',
  border: '1px solid',
  transition: 'border-color 0.3s ease',
};

const miniNumberStyle: React.CSSProperties = {
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'var(--font-sans)',
  fontWeight: 700,
  fontSize: '14px',
  color: '#0A0814',
  marginBottom: '10px',
};

const miniTitleStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontStyle: 'italic',
  fontSize: '14px',
  fontWeight: 500,
  textAlign: 'center',
};

export default HowItWorks;
